import React, { useMemo } from "react";
import { Route, RouteProps, Redirect, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState } from "./store";
import MenuService from "./services/MenuService";
import NotFoundPage from "./components/shared/TailwindComponents/NotFoundPage";

interface PrivateRouteProps extends RouteProps {
  component?: React.ComponentType<any>;
  render?: () => React.ReactNode;
  subscriptionId?: string;
  restricted?: boolean;
  allowedUserTypes?: string[] | boolean; // Deprecated: kept for backward compatibility
}

// The plan, pricing and pack pages and the Stripe return pages (PrivateRoutes
// in components/routes). Open to anyone signed in; the pages themselves send
// members to their admin instead of to checkout.
export const BUYING_PATHS = new Set([
  "/plans",
  "/plans/checkout",
  "/pricing",
  "/packs",
  "/packs/complete",
  "/payment-result",
]);

const PrivateRoute: React.FC<PrivateRouteProps> = ({
  component: Component,
  render: Render,
  subscriptionId,
  restricted = false,
  allowedUserTypes, // Deprecated
  ...rest
}) => {
  const userInfo = useSelector((s: RootState) => s.user.userInfo);
  const menuStructure = useSelector((s: RootState) => s.menu?.menuStructure);
  const location = useLocation();

  /*
    THE UI LANGUAGE HAS ONE SOURCE, and this was not it.

    Three mechanisms used to decide what language the app spoke, and they ran
    in an order nobody chose. `i18n` seeds from the `language` key in
    localStorage (the cache, written on every `languageChanged`);
    `useAuth0Profile` hydrates from `preferences.general_settings.language`
    (the stored setting, which is what Settings writes); and this effect
    applied `lists.preferences.Language` - a different field on a legacy
    preferences blob, not even declared on `IPreferences`, and not the UI
    language at all. It ran on every change to that blob, so it could flip the
    app's language back AFTER the profile had set it correctly. A Bulgarian
    user picking Bulgarian and finding themselves in English a moment later
    was this.

    Removed rather than reordered: the setting is hydrated once from the
    profile and cached in localStorage, and Settings and onboarding both write
    it back to `general_settings.language` when the user changes it. One
    source, one write, one read.
  */

  // Check route access based on Redux-cached menu structure (no API calls)
  const hasMenuAccess = useMemo(() => {
    // Special case: reset-password routes are always allowed
    if (location.pathname.startsWith("/reset-password/")) {
      return true;
    }

    // Root path "/" is always allowed for navigation redirects
    if (location.pathname === "/") {
      return true;
    }

    // The notifications CENTRE is the full-page view of the bell's panel — a
    // core surface for anyone signed in, not a licensed menu item, so it never
    // appears in the backend menu structure. Without this it failed the menu
    // check and 404'd on a direct link / refresh. Allow it like "/" above.
    if (location.pathname === "/notifications") {
      return true;
    }

    // Buying is never licence-gated, and none of these pages is a menu item:
    // Stripe Checkout returns to /plans?success=true, which 404'd here, so the
    // subscription sync on that page never ran and the new plan never showed.
    if (BUYING_PATHS.has(location.pathname)) {
      return true;
    }

    // Admin routes are accessible only to org admins
    if (location.pathname.startsWith("/admin")) {
      return userInfo?.is_org_admin === true;
    }

    // If menu is not yet loaded from Redux/persistence, deny access
    // (This will allow page to load with cached menu after page refresh)
    if (!menuStructure) {
      return false;
    }

    try {
      const accessibleRoutes = MenuService.extractAccessibleRoutes(menuStructure);

      // Route aliases: some features live under a different URL prefix
      // but share access with their parent menu item.
      const ROUTE_ALIASES: Record<string, string> = {
        '/playbooks': '/reviews',
      };

      let pathToCheck = location.pathname;
      for (const [alias, parent] of Object.entries(ROUTE_ALIASES)) {
        if (location.pathname.startsWith(alias)) {
          pathToCheck = parent + location.pathname.slice(alias.length);
          break;
        }
      }

      const hasAccess = MenuService.hasAccessToRoute(pathToCheck, accessibleRoutes);

      if (!hasAccess) {
        return false;
      }

      // Check if this route is admin-only and user is not admin
      const isAdminRoute = MenuService.isRouteAdminOnly(location.pathname, menuStructure);
      if (isAdminRoute && !userInfo?.is_org_admin) {
        return false;
      }

      return true;
    } catch (error) {
      console.error('[PrivateRoute] Error checking menu access:', error);
      return false;
    }
  }, [location.pathname, userInfo?.is_org_admin, menuStructure]);

  // Reset-password routes bypass menu check
  if (typeof rest.path === "string" && rest.path.startsWith("/reset-password/")) {
    return <Route {...rest} render={(p) => (Component ? <Component {...p} /> : Render ? Render() : null)} />;
  }

  // Check subscription restriction
  if (!subscriptionId && restricted) return <Redirect to="/plans" />;

  // Menu-based route access check - show 404 if no access
  if (!hasMenuAccess) {
    return <Route {...rest} render={() => <NotFoundPage />} />;
  }

  // Handle deprecated allowedUserTypes (for backward compatibility during migration)
  if (typeof allowedUserTypes === "boolean" && !allowedUserTypes) {
    return <Route {...rest} render={() => <NotFoundPage />} />;
  }

  const renderInner = (props: any) => (Component ? <Component {...props} /> : Render ? Render() : null);
  return <Route {...rest} render={renderInner} />;
};

export default PrivateRoute;
